import { getFullName } from '@provision/formatting-utils';
import dayjs from 'dayjs';
import { sortBy } from 'remeda';

import { RRule, rrulestr, Weekday } from 'rrule';
import {
  OncallManagementScheduleRecord,
  RecurrenceOptions,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import type { SignalManagementFormValues } from 'src/pages/Settings/signal-oncall-schedule/signal-oncall-schedule-modal';
import {
  CalendarEventObject,
  CalendarView,
} from 'src/pages/Settings/signal-oncall-schedule/types';

import {
  convertToUTCDate,
  getAgendaBoundariesInSpecifiedTimezone,
  getDayBoundariesInSpecifiedTimezone,
  getWeekBoundariesInSpecifiedTimezone,
  isAllDaySchedule,
  treatUTCDateAsLocal,
} from './dates-manipulation';
import { EventTitle } from './ui-elements';

const capitalizeFirstLetter = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

function sortAgendaEvents(events: CalendarEventObject[]): CalendarEventObject[] {
  // Stable sort from least-significant key to most-significant key.
  return sortBy((event: CalendarEventObject) => dayjs(event.start).startOf('day').valueOf())(
    sortBy((event: CalendarEventObject) => (isAllDaySchedule(event) ? 0 : 1))(
      sortBy((event: CalendarEventObject) => event.start.getTime())(
        sortBy((event: CalendarEventObject) => event.end.getTime())(
          sortBy((event: CalendarEventObject) => event.id)(events),
        ),
      ),
    ),
  );
}

function getRecurrenceDescription(recurrence: RecurrenceOptions): string {
  if (recurrence === RecurrenceOptions.Once) {
    return 'Does not repeat';
  }

  return capitalizeFirstLetter(rrulestr(recurrence).toText());
}

export function mapScheduleToEvents(
  schedule: OncallManagementScheduleRecord[],
): CalendarEventObject[] {
  /*
    Schedules are coming from backend in form of UTC date strings.
    At first they're converted to regular date objects, meaning
    that they are in local timezone.

    In the DzOncallSchedule component the start and end dates
    will be adjusted according to partner's timezone,
    because internally react-big-calendar uses current browser timezone
    but we want to let the user operate as in partner's timezone,
    meaning creating events in that tz.
  */
  return schedule.map((event) => {
    return {
      id: event.id,
      title: <EventTitle event={event} />,
      tooltip: `\nRecurrence: ${getRecurrenceDescription(
        event.recurrence as RecurrenceOptions,
      )}\nPrimary On-Call Agent: ${getFullName(event.primaryAgent)}`,
      start: new Date(event.startTime),
      end: new Date(event.endTime),
      primaryAgentId: event.primaryAgent.id,
      secondaryAgentId: event.secondaryAgent?.id,
      recurrence: event.recurrence as RecurrenceOptions,
    };
  });
}

export function generateRecurringEvent(
  event: CalendarEventObject,
  rangeStart: Date,
  rangeEnd: Date,
): CalendarEventObject[] {
  /**
   * According to rrule's docs, all calculations should be perfomed in UTC
   * to avoid DST problems. However the calendar works with events in local
   * time. So we need to perform conversion to UTC back and forth.
   */
  const utcRangeStart = convertToUTCDate(rangeStart);
  const utcRangeEnd = convertToUTCDate(rangeEnd);
  const rule = rrulestr(event.recurrence, {
    dtstart: convertToUTCDate(event.start),
  });

  const until = event.until ? convertToUTCDate(event.until) : undefined;

  const recurringDays = rule
    // 3rd argument is set to `true` to generate dates that lie on the boundaries,
    // otherwise they will be `undefined`.
    .between(utcRangeStart, utcRangeEnd, true)
    .map((start) => ({
      start,
      end: dayjs(
        new Date(start.getTime() + dayjs(event.end).diff(dayjs(event.start), 'ms')),
      ).toDate(),
    }));

  return recurringDays.reduce<CalendarEventObject[]>((acc, { start, end }) => {
    if (!start || !end) return acc;
    if (start && until && start > until) return acc;

    return acc.concat({
      ...event,
      start: treatUTCDateAsLocal(start),
      end: treatUTCDateAsLocal(end),
    });
  }, []);
}

export function getDisplayedEventsForSelectedRange(
  events: CalendarEventObject[],
  selectedDate: Date,
  view: CalendarView,
): CalendarEventObject[] {
  /*
    Internally react-big-calendar uses current browser timezone to filter
    events that fall in a range of selected view (day or week).
    Using `dayjs.tz.guess()` we get the timezone of the browser,
    and then we filter existing events that fall within this range
    and generate recurring events for the selected range.

    `events` and `selectedDate` already come in a browser timezone.
    The array of events is generated from UTC date strings coming from backend,
    and converted to local dates. Selected date is a date object created upon
    interaction with the calendar component.
  */
  const getBoundaries = () => {
    if (view === 'day') {
      return getDayBoundariesInSpecifiedTimezone(selectedDate, dayjs.tz.guess());
    }
    if (view === 'agenda') {
      return getAgendaBoundariesInSpecifiedTimezone(selectedDate, dayjs.tz.guess());
    }
    return getWeekBoundariesInSpecifiedTimezone(selectedDate, dayjs.tz.guess());
  };
  const [rangeStart, rangeEnd] = getBoundaries();

  const displayedEvents = events.reduce<CalendarEventObject[]>((acc, event) => {
    // Events that happen only once don't have a recurrence rule.
    if (event.recurrence === RecurrenceOptions.Once) {
      if (event.start <= rangeEnd && event.end > rangeStart) {
        acc.push(event);
        return acc;
      }
      return acc;
    }

    const recurringEvents = generateRecurringEvent(event, rangeStart, rangeEnd);

    acc.push(
      ...recurringEvents.filter(
        (occurrence) => occurrence.start <= rangeEnd && occurrence.end > rangeStart,
      ),
    );
    return acc;
  }, []);

  if (view !== 'agenda') {
    return displayedEvents;
  }

  return sortAgendaEvents(displayedEvents);
}

export function mapScheduleToRruleString(
  schedule: SignalManagementFormValues,
  recurringDays: Weekday[],
): string {
  if (schedule.recurrence === RecurrenceOptions.Custom) {
    const { recurrenceInterval, recurrencePeriod } = schedule;
    const rrule = new RRule({
      freq: recurrencePeriod,
      interval: recurrenceInterval ? Number(recurrenceInterval) : undefined,
      wkst: RRule.SU,
      byweekday: recurringDays,
    });
    return rrule.toString();
  }

  const parsed = rrulestr(schedule.recurrence, {
    dtstart: schedule.dateStart.toDate(),
  });

  const rrule = new RRule({
    freq: parsed.options.freq,
    interval: parsed.options.interval,
    wkst: RRule.SU,
    byweekday: recurringDays,
  });

  return rrule.toString();
}

export function getRecurrenceUntil(recurrence: string): string | null {
  if (!recurrence || recurrence === 'once') {
    return null;
  }

  const parsed = rrulestr(recurrence);
  return parsed.options.until ? parsed.options.until.toISOString() : null;
}

export function getLimitedRecurrence(recurrence: string, date: string | null): string {
  const parsed = rrulestr(recurrence);
  const rrule = new RRule({
    ...parsed.origOptions,
    until: date ? new Date(date) : undefined,
  });

  return rrule.toString();
}
